/** Console-side types for the People API (specs/006-user-management/contracts/account-people-api.md). */

/** Staff roles, highest first (auth-module `STAFF_ROLES`). */
export const STAFF_ROLES = [
  "platform_admin",
  "district_admin",
  "school_admin",
  "teacher",
] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** Roles an invitation can carry; platform admins are not invited into a district. */
export const INVITABLE_ROLES: readonly StaffRole[] = [
  "district_admin",
  "school_admin",
  "teacher",
];

export type StaffStatus = "active" | "invited" | "deactivated";
export type AssignmentRole = "teacher" | "aide";

/** A list route's `data`: one page of a filtered list. */
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/** Paging and search every list route takes. */
export interface PageQuery {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface OrganizationRef {
  id: string;
  name: string;
  type: "district" | "school";
}

export interface StaffSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: StaffRole[];
  organization: OrganizationRef;
  status: StaffStatus;
  classCount: number;
  lastActiveAt?: string | null;
  sso: boolean;
}

export interface ClassAssignment {
  classId: string;
  className: string;
  schoolId: string;
  schoolName: string;
  role: AssignmentRole;
  source: "roster" | "school";
  active: boolean;
  assignedAt: string;
  endedAt?: string | null;
}

export interface StaffAllowedActions {
  edit: boolean;
  deactivate: boolean;
  reactivate: boolean;
  resetPassword: boolean;
  grantableRoles: StaffRole[];
}

export interface StaffDetail extends StaffSummary {
  updatedAt: string;
  assignments: ClassAssignment[];
  allowedActions: StaffAllowedActions;
}

/** `POST /v1/staff/:id/reactivate` adds the classes the person had before deactivation. */
export interface ReactivatedStaff extends StaffDetail {
  inactiveAssignments: ClassAssignment[];
}

export interface ListStaffArgs extends PageQuery {
  districtId?: string;
  schoolId?: string;
  roles?: StaffRole[];
  status?: StaffStatus;
  classId?: string;
}

export interface UpdateStaffPayload {
  firstName?: string;
  lastName?: string;
  roles?: StaffRole[];
  organizationId?: string;
  expectedUpdatedAt?: string;
}

export interface StaffClassAssignmentInput {
  classId: string;
  role: AssignmentRole;
}

export type BulkStaffAction = "deactivate" | "reactivate" | "assign-classes";

export interface BulkStaffPayload {
  action: BulkStaffAction;
  ids: string[];
  classIds?: string[];
  role?: AssignmentRole;
}

export interface BulkItemResult {
  id: string;
  status: "ok" | "refused";
  code?: string;
  message?: string;
}

export interface BulkResult {
  results: BulkItemResult[];
}

export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface InvitationSummary {
  id: string;
  email: string;
  role: StaffRole;
  districtId: string;
  organization: OrganizationRef;
  classes: { id: string; name: string }[];
  status: InvitationStatus;
  invitedBy: { id: string; name: string } | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
}

export interface ListInvitationsArgs extends PageQuery {
  districtId?: string;
  schoolId?: string;
  role?: StaffRole;
  status?: InvitationStatus;
}

/** `GET /v1/classes` row. */
export interface ClassListItem {
  id: string;
  name: string;
  period: string | null;
  gradeLevel: string | null;
  schoolYear?: string;
  schoolId: string;
  schoolName: string | null;
  source: "roster" | "manual";
  active: boolean;
  studentCount: number;
  staff: { id: string; name: string; role: AssignmentRole }[];
}

export interface ListClassesArgs extends PageQuery {
  districtId?: string;
  schoolId?: string;
  includeInactive?: boolean;
}

/**
 * A person's display name. Falls back to their email when the profile has no name (a roster
 * placeholder or an account created before names were required), so lists, panel titles and the
 * name button's accessible name are never blank.
 */
export const fullName = (person: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) =>
  `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim() ||
  person.email ||
  "";

/** The highest staff role a person holds, for single-role displays and pickers. */
export const primaryRole = (roles: readonly string[]): StaffRole | undefined =>
  STAFF_ROLES.find((role) => roles.includes(role));

/**
 * Roles a caller may grant, mirroring account-service `grantableRoles` (staff-role-policy). The
 * console only uses it to choose which options to show; the server re-checks every call.
 */
export const grantableRolesFor = (callerRoles: readonly string[]) => {
  if (callerRoles.includes("platform_admin")) return [...STAFF_ROLES];
  if (callerRoles.includes("district_admin")) {
    return ["district_admin", "school_admin", "teacher"] as StaffRole[];
  }
  if (callerRoles.includes("school_admin")) {
    return ["school_admin", "teacher"] as StaffRole[];
  }
  return [] as StaffRole[];
};

/** Query string with repeated keys for arrays and no empty values. */
export const toQueryString = (args: object) => {
  const params = new URLSearchParams();
  Object.entries(args).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : "";
};
